<template>
  <div>
    <LoadingSpinner v-if="loading" size="large" message="Loading albums..." />
    
    <!-- Search Section - Always Visible at Top -->
    <div class="search-section">
      <div class="search-container">
        <input 
          id="album-search"
          type="text" 
          v-model="searchTerm" 
          placeholder="Search by artist or title..."
          class="search-input"
          @keydown.escape="searchTerm = ''"
          aria-label="Search albums by artist or title"
        >
        <button 
          v-if="searchTerm"
          @click="searchTerm = ''"
          class="search-clear"
          aria-label="Clear search"
        >
          <span>×</span>
        </button>
      </div>
    </div>

    <!-- Collapsible Filter Panel -->
    <div class="filter-panel">
      <button 
        class="add-filters-toggle" 
        @click="filtersExpanded = !filtersExpanded"
        :aria-expanded="filtersExpanded"
      >
        <span>{{ filtersExpanded ? 'Hide Filters' : 'Add Filters' }}</span>
        <span class="toggle-icon" :class="{ expanded: filtersExpanded }">▼</span>
      </button>
      
      <!-- Expanded Filter Options -->
      <div v-show="filtersExpanded" class="filter-options">
        <!-- Filter Type Toggle -->
        <div class="filter-type-toggle">
          <button 
            :class="{ active: filterType === 'ratings' }" 
            @click="filterType = 'ratings'"
            class="filter-type-btn"
          >
            Ratings by Reviewer
          </button>
          <button
            :class="{ active: filterType === 'submitted' }"
            @click="filterType = 'submitted'"
            class="filter-type-btn"
          >
            Submitted by User
          </button>
          <button
            :class="{ active: filterType === 'genre' }"
            @click="filterType = 'genre'"
            class="filter-type-btn"
          >
            Genre
          </button>
        </div>
        
        <!-- Name Filter Buttons -->
        <div v-if="filterType" class="reviewer-filters">
          <div v-if="filterType === 'ratings'" class="filter-names-section">
            <p class="filter-description">Show albums rated by specific reviewer:</p>
            <div class="filter-names">
              <button 
                v-for="name in individualTierNames" 
                :key="`rating-${name}`" 
                :class="{ active: currentTierGrouping === name }"
                @click="setTierGrouping(name)"
                class="reviewer-btn"
              >
                {{ capitalize(name) }}
              </button>
            </div>
          </div>
          <div v-if="filterType === 'submitted'" class="filter-names-section">
            <p class="filter-description">Show albums submitted by specific user:</p>
            <div class="filter-names">
              <button
                v-for="name in submitterNames"
                :key="`submitter-${name}`"
                :class="{ active: selectedSubmitter === name }"
                @click="setSubmitterFilter(name)"
                class="reviewer-btn"
              >
                {{ capitalize(name) }}
              </button>
            </div>
          </div>
          <div v-if="filterType === 'genre'" class="filter-names-section">
            <p class="filter-description">Show albums with main genre:</p>
            <div class="filter-names">
              <button
                v-for="slug in availableMainGenres"
                :key="slug"
                :class="{ active: selectedMainGenre === slug }"
                @click="setMainGenreFilter(slug)"
                class="reviewer-btn"
              >
                {{ slugToLabel(slug) }}
              </button>
            </div>
          </div>
        </div>

        <!-- Active Filters Display Within Panel -->
        <div v-if="hasActiveFilters" class="active-filters-within-panel">
          <p class="active-filters-panel-title">Tier View Filters</p>
          <div class="active-filters-chips">
            <FilterChip 
              v-if="searchTerm" 
              :label="`Search: ${searchTerm}`" 
              @remove="searchTerm = ''"
            />
            <FilterChip 
              v-if="currentTierGrouping && currentTierGrouping !== 'clear'" 
              :label="`Rated by: ${capitalize(currentTierGrouping)}`" 
              @remove="setTierGrouping('clear')"
            />
            <FilterChip
              v-if="selectedSubmitter"
              :label="`Submitted by: ${capitalize(selectedSubmitter)}`"
              @remove="setSubmitterFilter(null)"
            />
            <FilterChip
              v-if="selectedMainGenre"
              :label="`Genre: ${slugToLabel(selectedMainGenre)}`"
              @remove="setMainGenreFilter(selectedMainGenre)"
            />
            <button class="clear-all-filters" @click="clearAllFilters()" aria-label="Clear all filters">
              Clear All
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- No Results Display -->
    <div v-if="!loading && filteredAlbumsByTier.length === 0" class="no-results">
      <div class="no-results-icon">🔍</div>
      <h3>No albums found</h3>
      <p>Try adjusting your filters or search terms to find more albums.</p>
      <button class="clear-all-btn" @click="clearAllFilters()">Clear All Filters</button>
    </div> 
    <div v-for="[tier, albums] in filteredAlbumsByTier" :key="tier" :class="['tier-container', 'modern-tier-container', tierClass(tier)]">
      <div class="tier-title">
        <EnhancedTierBadge 
          :tier="tier" 
          variant="medal" 
          size="large"
          :glow-effect="true"
        />
        <button 
          class="tier-expand-btn" 
          @click="openExpandedView(tier, albums)"
          :aria-label="`Expand ${tier} tier to fullscreen view`"
          title="Expand tier to fullscreen view"
        >
          <Expand :size="18" class="expand-icon" />
        </button>
      </div>
      <div class="albums-scroll-wrapper">
        <div class="albums-scroll-container" ref="albumsContainerRef">
          <albumCover 
            v-for="album in albums" 
            :key="`${album.id}-${album.tier}-${album.cover_url}`"
            :album="album" 
            :veto="album.veto === true" 
            :showTierBorder="true" 
            @albumClicked="handleAlbumClicked"
          ></albumCover>
        </div>
      </div>
    </div>


    <AlbumDetailsModal v-if="selectedAlbum" :album="selectedAlbum" :veto="selectedAlbum.veto" :veto_by="selectedAlbum.veto_by" @close="selectedAlbum = null" />
    <ExpandedTierView 
      v-if="showExpandedView" 
      :tier="expandedTier" 
      :albums="expandedAlbums" 
      @close="closeExpandedView"
      @albumClicked="handleAlbumClicked"
    />
  </div>
</template>

<script setup>
import { useTierRow } from '../composables/useTierRow';
import albumCover from './albumCover.vue';
import AlbumDetailsModal from './modals/AlbumDetailsModal.vue';
import ExpandedTierView from './ExpandedTierView.vue';
import LoadingSpinner from './LoadingSpinner.vue';
import EnhancedTierBadge from './EnhancedTierBadge.vue';
import FilterChip from './FilterChip.vue';
import { capitalize } from '../utils/capitalize';
import { ref, computed } from 'vue';
import { Expand } from 'lucide-vue-next';

const {
  currentTierGrouping,
  filteredAlbumsByTier,
  individualTierNames,
  submitterNames,
  availableMainGenres,
  searchTerm,
  selectedAlbum,
  albumsContainerRef,
  handleAlbumClicked,
  tierClass,
  setTierGrouping,
  setSubmitterFilter,
  setMainGenreFilter,
  filterType, // Use filterType from composable
  loading, // Add loading state
  selectedSubmitter, // Add selectedSubmitter from composable
  selectedMainGenre, // Add selectedMainGenre from composable
} = useTierRow();

// Enhanced filter functionality
const filtersExpanded = ref(false);

// Expanded tier view functionality
const showExpandedView = ref(false);
const expandedTier = ref('');
const expandedAlbums = ref([]);

const hasActiveFilters = computed(() => {
  return searchTerm.value ||
         (currentTierGrouping.value && currentTierGrouping.value !== 'clear') ||
         selectedSubmitter.value ||
         !!selectedMainGenre.value;
});

const clearAllFilters = () => {
  searchTerm.value = '';
  setTierGrouping('clear');
  setSubmitterFilter(null);
  setMainGenreFilter(selectedMainGenre.value); // toggles off (sets to null if already set)
};

function slugToLabel(slug) {
  if (!slug) return '';
  return slug.split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
    .replace('And', '&')
    .replace('R & B', 'R&B');
}

const openExpandedView = (tier, albums) => {
  expandedTier.value = tier;
  expandedAlbums.value = albums;
  showExpandedView.value = true;
};

const closeExpandedView = () => {
  showExpandedView.value = false;
  expandedTier.value = '';
  expandedAlbums.value = [];
};


</script>


<style scoped>
  .tier-title {
    font-size: 18px;
    font-weight: 700;
    display: flex;
    padding: 12px 20px;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(8px);
    border-radius: 12px 12px 0 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    letter-spacing: 0.5px;
    color: #ffffff;
  }

  /* Tier-specific subtle header backgrounds */
  .tier-s .tier-title {
    background: rgb(93, 1, 1, 0.2); /* S tier - subtle red */
  }

  .tier-a .tier-title {
    background: rgb(140, 61, 0, 0.2); /* A tier - subtle orange */
  }

  .tier-b .tier-title {
    background: rgb(126, 113, 0, 0.2); /* B tier - subtle yellow */
  }

  .tier-c .tier-title {
    background: rgb(50, 82, 32, 0.2); /* C tier - subtle green */
  }

  .tier-d .tier-title {
    background: rgb(0, 60, 89, 0.2); /* D tier - subtle blue */
  }

  .tier-e .tier-title {
    background: rgb(44, 51, 57, 0.2); /* E tier - subtle gray */
  }

  .tier-not-rated-yet .tier-title {
    background: rgb(67, 67, 67, 0.2); /* Not rated - subtle gray */
  }

  .tier-now-listening-to .tier-title {
    background: rgb(58, 39, 98, 0.2); /* Now listening - subtle purple */
  }

  /* Legacy tier names for backward compatibility */
  .tier-tiering .tier-title,
  .tier-not .tier-title,
  .tier-question .tier-title {
    background: rgb(67, 67, 67, 0.2); /* Not rated - subtle gray */
  }

  .tier-now .tier-title {
    background: rgb(58, 39, 98, 0.2); /* Now listening - subtle purple */
  }

  .tier-expand-btn {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.8);
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(8px);
    flex-shrink: 0;
    margin-left: auto;
  }

  .tier-expand-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.4);
    color: #ffffff;
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(255, 255, 255, 0.2);
  }

  .tier-expand-btn:active {
    transform: scale(0.95);
  }

  .tier-expand-btn .expand-icon {
    transition: transform 0.2s ease;
    stroke: currentColor;
    fill: none;
    width: 18px !important;
    height: 18px !important;
    min-width: 18px;
    min-height: 18px;
    display: block;
    stroke-width: 2;
  }

  .tier-expand-btn:hover .expand-icon {
    transform: scale(1.1);
  }
  
  .tier-title__text {
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.8px;
  }
  .tier-row {
    display: flex;
    align-items: stretch;
    text-align: left;
    background-color: rgb(41, 41, 41);
  }

  /* Search Section - Always Visible */
  .search-section {
    margin-bottom: 16px;
  }

  /* Enhanced Search Input */
  .search-container {
    width: 100%;
    position: relative;
  }

  .search-input {
    width: 100%;
    min-height: 48px;
    background: rgba(255, 255, 255, 0.08);
    border: 2px solid rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    padding: 14px 50px 14px 20px; /* Add padding for clear button */
    color: #f3f3f3;
    font-size: 16px;
    font-weight: 500;
    transition: all 0.3s ease;
    backdrop-filter: blur(12px);
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  /* Clear Button */
  .search-clear {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: rgba(243, 243, 243, 0.6);
    font-size: 20px;
    font-weight: bold;
  }

  .search-clear:hover {
    color: rgba(243, 243, 243, 0.9);
    transform: translateY(-50%) scale(1.2);
  }

  .search-input:focus {
    outline: none;
    border-color: rgba(45, 140, 240, 0.6);
    background: rgba(255, 255, 255, 0.12);
    box-shadow: 0 0 0 4px rgba(45, 140, 240, 0.1), inset 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .search-input::placeholder {
    color: rgba(243, 243, 243, 0.5);
    font-weight: 400;
  }

  /* Collapsible Filter Panel */
  .filter-panel {
    background: rgba(255, 255, 255, 0.04);
    backdrop-filter: blur(16px);
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    margin-bottom: 24px;
    overflow: hidden;
  }

  .add-filters-toggle {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: transparent;
    border: none;
    color: #f3f3f3;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    min-height: 56px;
  }

  .add-filters-toggle:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  .toggle-icon {
    font-size: 14px;
    transition: transform 0.3s ease;
  }

  .toggle-icon.expanded {
    transform: rotate(180deg);
  }

  .filter-options {
    padding: 0 20px 20px 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    animation: slideDown 0.3s ease;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Filter Type Toggle Buttons - Smaller */
  .filter-type-toggle {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 16px;
    margin-top: 16px;
  }

  .filter-type-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, rgba(45, 140, 240, 0.1) 0%, rgba(45, 140, 240, 0.05) 100%);
    border: 2px solid rgba(45, 140, 240, 0.3);
    border-radius: 10px;
    color: #f3f3f3;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(8px);
    min-height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .filter-type-btn.active {
    background: linear-gradient(135deg, rgba(45, 140, 240, 0.9) 0%, rgba(45, 140, 240, 0.7) 100%);
    border-color: rgba(45, 140, 240, 0.8);
    color: white;
    box-shadow: 0 3px 12px rgba(45, 140, 240, 0.3);
    transform: translateY(-1px);
  }

  .filter-type-btn:hover:not(.active) {
    background: linear-gradient(135deg, rgba(45, 140, 240, 0.2) 0%, rgba(45, 140, 240, 0.1) 100%);
    border-color: rgba(45, 140, 240, 0.5);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(45, 140, 240, 0.2);
  }

  /* Reviewer Filter Section */
  .reviewer-filters {
    margin-top: 4px;
  }

  .filter-names-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .filter-description {
    font-size: 13px;
    color: rgba(243, 243, 243, 0.6);
    margin: 0;
    line-height: 1.4;
  }

  .filter-names {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    justify-content: flex-start;
  }

  /* Old filter-names button styles removed - replaced with new purple theme above */

  /* Legacy styles removed - replaced with new enhanced filter system */

  .filter-toggle button {
    background: linear-gradient(135deg, rgba(45, 140, 240, 0.8) 0%, rgba(45, 140, 240, 0.6) 100%);
    color: white;
    padding: 10px 16px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 8px;
    border: 1px solid rgba(45, 140, 240, 0.3);
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(8px);
    position: relative;
    overflow: hidden;
  }

  .filter-toggle button.active {
    background: linear-gradient(135deg, #2d8cf0 0%, #1e40af 100%);
    box-shadow: 0 4px 12px rgba(45, 140, 240, 0.3);
    transform: translateY(-1px);
  }

  .filter-toggle button:hover:not(.active) {
    background: linear-gradient(135deg, rgba(45, 140, 240, 0.9) 0%, rgba(45, 140, 240, 0.7) 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(45, 140, 240, 0.2);
  }

  /* Reviewer Filter Buttons - Smaller */
  .reviewer-btn {
    padding: 6px 12px;
    background: linear-gradient(135deg, rgba(139, 69, 193, 0.15) 0%, rgba(124, 58, 237, 0.1) 100%);
    border: 2px solid rgba(139, 69, 193, 0.4);
    border-radius: 8px;
    color: #f3f3f3;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    min-width: 70px;
    min-height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(8px);
  }

  .reviewer-btn.active {
    background: linear-gradient(135deg, rgba(139, 69, 193, 0.9) 0%, rgba(124, 58, 237, 0.8) 100%);
    border-color: rgba(139, 69, 193, 0.8);
    color: white;
    box-shadow: 0 3px 12px rgba(139, 69, 193, 0.4);
    transform: translateY(-1px);
  }

  .reviewer-btn:hover:not(.active) {
    background: linear-gradient(135deg, rgba(139, 69, 193, 0.25) 0%, rgba(124, 58, 237, 0.2) 100%);
    border-color: rgba(139, 69, 193, 0.6);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(139, 69, 193, 0.3);
  }

  /* Active Filters Within Panel */
  .active-filters-within-panel {
    margin-top: 24px;
    padding: 20px;
    background: rgba(45, 140, 240, 0.08);
    border: 1px solid rgba(45, 140, 240, 0.2);
    border-radius: 12px;
    backdrop-filter: blur(8px);
    animation: slideInFromTop 0.3s ease;
  }

  .active-filters-panel-title {
    font-size: 13px;
    font-weight: 600;
    color: rgba(243, 243, 243, 0.8);
    margin: 0 0 12px 0;
    text-align: center;
    line-height: 1.4;
  }

  .active-filters-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    justify-content: center;
  }

  .clear-all-filters {
    padding: 8px 16px;
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    min-height: 36px;
  }

  .clear-all-filters:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(239, 68, 68, 0.3);
  }

  @keyframes slideInFromTop {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Enhanced Search Input */
  .search-container {
    width: 100%;
    position: relative;
  }

  .search-input {
    width: 100%;
    min-height: 48px;
    background: rgba(255, 255, 255, 0.08);
    border: 2px solid rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    padding: 14px 20px;
    color: #f3f3f3;
    font-size: 16px;
    font-weight: 500;
    transition: all 0.3s ease;
    backdrop-filter: blur(12px);
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .search-input:focus {
    outline: none;
    border-color: rgba(45, 140, 240, 0.6);
    background: rgba(255, 255, 255, 0.12);
    box-shadow: 0 0 0 4px rgba(45, 140, 240, 0.1), inset 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .search-input::placeholder {
    color: rgba(243, 243, 243, 0.5);
    font-weight: 400;
  }

  /* Results Status Section */
  .results-status {
    margin-bottom: 24px;
  }

  .selected-tier-filter {
    display: flex;
    font-size: 26px;
    font-weight: 700;
    text-align: left;
    align-items: center;
    padding: 16px 0;
    flex-basis: 100%;
    background: linear-gradient(135deg, #60a5fa, #34d399);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }

  /* No Results Display */
  .no-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    background: rgba(255, 255, 255, 0.02);
    border: 2px dashed rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    margin: 24px 0;
    text-align: center;
    animation: fadeInUp 0.5s ease;
  }

  .no-results-icon {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.6;
  }

  .no-results h3 {
    font-size: 20px;
    font-weight: 600;
    color: #f3f3f3;
    margin: 0 0 8px 0;
  }

  .no-results p {
    font-size: 14px;
    color: rgba(243, 243, 243, 0.7);
    margin: 0 0 24px 0;
    line-height: 1.5;
  }

  .clear-all-btn {
    padding: 12px 24px;
    background: linear-gradient(135deg, rgba(45, 140, 240, 0.9) 0%, rgba(45, 140, 240, 0.7) 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    min-height: 44px;
  }

  .clear-all-btn:hover {
    background: linear-gradient(135deg, rgba(45, 140, 240, 1) 0%, rgba(45, 140, 240, 0.8) 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(45, 140, 240, 0.4);
  }

  /* Enhanced Transitions */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .modern-tier-container {
    border-radius: 16px;
    margin-bottom: 24px;
    overflow: hidden;
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    animation: slideInFromBottom 0.6s ease;
  }

  .modern-tier-container:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  }

  @keyframes slideInFromBottom {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .albums-scroll-wrapper {
    position: relative;
    overflow: visible; /* Allow all overflow for hover effects */
    margin-bottom: 0; /* Remove bottom margin to eliminate light bar */
    background: rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(12px);
    padding: 0; /* No padding for maximum space efficiency */
  }

  .albums-scroll-container {
    display: flex;
    overflow-x: auto;
    overflow-y: visible; /* Ensure vertical overflow is visible */
    gap: 8px; /* Further reduced gap for space efficiency */
    padding: 16px 12px 20px 12px; /* Optimized padding - enough for hover effects */
    scrollbar-width: thin;
    align-items: flex-start;
  }

  .albums-scroll-wrapper::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 100px;
    pointer-events: none;
    background-image: linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.2));
  }

  /* Enhanced tier-specific styles using CSS custom properties */
  /* Updated to match sessions view styling (black backgrounds with neon borders) */
  .tier-s {
    background: #232323; /* Black background like sessions view */
    box-shadow: var(--tier-hover-shadow-base) var(--tier-s-glow);
  }

  .tier-a {
    background: #232323; /* Black background like sessions view */
    box-shadow: var(--tier-hover-shadow-base) var(--tier-a-glow);
  }

  .tier-b {
    background: #232323; /* Black background like sessions view */
    box-shadow: var(--tier-hover-shadow-base) var(--tier-b-glow);
  }

  .tier-c {
    background: #232323; /* Black background like sessions view */
    box-shadow: var(--tier-hover-shadow-base) var(--tier-c-glow);
  }

  .tier-d {
    background: #232323; /* Black background like sessions view */
    box-shadow: var(--tier-hover-shadow-base) var(--tier-d-glow);
  }

  .tier-e {
    background: #232323; /* Black background like sessions view */
    box-shadow: var(--tier-hover-shadow-base) var(--tier-e-glow);
  }

  .tier-not-rated-yet {
    background: #232323; /* Black background like sessions view */
    box-shadow: var(--tier-hover-shadow-base) var(--tier-not-rated-glow);
  }

  .tier-now-listening-to {
    background: #232323; /* Black background like sessions view */
    box-shadow: var(--tier-hover-shadow-base) var(--tier-now-listening-glow);
  }

  /* Legacy tier names for backward compatibility */
  .tier-tiering {
    background: #232323; /* Black background like sessions view */
    box-shadow: var(--tier-hover-shadow-base) var(--tier-not-rated-glow);
  }

  .tier-not {
    background: #232323; /* Black background like sessions view */
    box-shadow: var(--tier-hover-shadow-base) var(--tier-not-rated-glow);
  }

  .tier-now {
    background: #232323; /* Black background like sessions view */
    box-shadow: var(--tier-hover-shadow-base) var(--tier-now-listening-glow);
  }

  .tier-question {
    background: #232323; /* Black background like sessions view */
    box-shadow: var(--tier-hover-shadow-base) var(--tier-not-rated-glow);
  }

  /* Mobile Responsiveness */
  @media (max-width: 768px) {
    .search-input {
      min-height: 52px;
      font-size: 16px;
      padding: 16px 50px 16px 20px;
    }
    
    .search-clear {
      width: 32px;
      height: 32px;
      right: 10px;
    }

    .tier-expand-btn {
      width: 40px;
      height: 40px;
    }

    .add-filters-toggle {
      min-height: 60px;
      padding: 18px 20px;
    }

    .filter-type-toggle {
      display: grid;
      grid-template-columns: 1fr;
      gap: 8px;
    }

    .filter-type-btn {
      min-height: 42px;
      padding: 10px 16px;
      font-size: 14px;
    }

    .filter-names {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
      gap: 8px;
    }

    .reviewer-btn {
      min-height: 40px;
      padding: 8px;
      font-size: 14px;
    }

    .active-filters-panel-title {
      font-size: 12px;
    }

    .active-filters-chips {
      justify-content: center;
    }
  }

  @media (max-width: 480px) {
    .filter-panel {
      border-radius: 12px;
    }

    .filter-names {
      grid-template-columns: 1fr;
    }

    .reviewer-btn {
      min-height: 44px;
      width: 100%;
    }

    .filter-type-btn {
      min-height: 48px;
      width: 100%;
    }

    .active-filters-panel-title {
      font-size: 11px;
    }
  }
</style>
